import { SlPopup } from "@shoelace-style/shoelace";
import "@shoelace-style/shoelace/dist/components/divider/divider.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/menu-label/menu-label.js";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import shoelaceDark from "@shoelace-style/shoelace/dist/themes/dark.css?inline";
import shoelaceLight from "@shoelace-style/shoelace/dist/themes/light.css?inline";

import { LitElement, PropertyValues, css, html, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { join } from "lit/directives/join.js";
import { map } from "lit/directives/map.js";
import { repeat } from "lit/directives/repeat.js";
import { EditorView } from "prosemirror-view";

import { type MatchInput } from "..";
import { MenuItemState } from "../../../core/menu";
import {
    FlatMenuItem,
    filterFlatItems,
    flattenInlineMenuItems,
} from "./flatten";
import { SubMenu } from "./insert-submenu";

/**
 * Overwrite the run type for MenuItemState.
 */
export type InputMenuItemState = Omit<
    MenuItemState,
    "run" | "items" | "getItems"
> & {
    run: (event: Event, input: MatchInput) => void;
    items: Omit<InputMenuItemState, "group" | "showInInlineMenu">[];
    getItems: () => Promise<InputMenuItemState["items"]>;
};

/**
 * Returns an icon template from the given name.
 */
export const iconTemplate = ({
    name,
    library = "component-assets",
    slot = "prefix",
}: {
    name: string;
    library?: string;
    slot?: string;
}) => html` <sl-icon name=${name} library=${library} slot=${slot}></sl-icon>`;

/**
 * Returns the menu items sorted based on the order property.
 */
export const sortItems = <T extends { order?: number }>(
    items: T[] = []
): T[] => {
    return items.toSorted((a, b) => {
        return (
            (a.order ?? Number.MAX_SAFE_INTEGER) -
            (b.order ?? Number.MAX_SAFE_INTEGER)
        );
    });
};

@customElement("c-node-insert-menu")
class NodeInsertMenu extends LitElement {
    @state()
    selection: {
        groupIndex: number;
        itemIndex: number;
    } = { groupIndex: 0, itemIndex: 0 };

    @state()
    input: MatchInput;

    @state()
    private darkMode = false;

    @property()
    editorView: EditorView;

    @property()
    items: InputMenuItemState[];

    @state()
    private flatItems?: FlatMenuItem[];

    private lastQuery = "";
    private themeObserver?: MutationObserver;

    constructor(
        items: InputMenuItemState[],
        input: MatchInput,
        editorView: EditorView
    ) {
        super();
        this.input = input;
        this.items = items;
        this.editorView = editorView;
        // Resolve the flattened leaf list eagerly so it is (usually) ready by
        // the first keystroke of a query. Entity managers cache their
        // entities, so this settles quickly after the first open.
        flattenInlineMenuItems(this.items).then(
            (flat) => {
                this.flatItems = flat;
            },
            () => {
                this.flatItems = [];
            }
        );
    }

    private get query() {
        return this.input?.query ?? "";
    }

    /** Typed-query mode: a flat filtered list instead of the grouped menu. */
    private get flatMode() {
        return this.query.length > 0;
    }

    private get filteredFlatItems(): FlatMenuItem[] | undefined {
        return this.flatItems
            ? filterFlatItems(this.flatItems, this.query)
            : undefined;
    }

    willUpdate(changedProperties: PropertyValues) {
        // Filtering shrinks/reorders the list, so a carried-over selection
        // could dangle — reset it whenever the query changes.
        if (changedProperties.has("input") && this.lastQuery !== this.query) {
            this.lastQuery = this.query;
            this.selection = { groupIndex: 0, itemIndex: 0 };
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.syncDarkMode();
        this.themeObserver = new MutationObserver(() => this.syncDarkMode());
        this.themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });
    }

    disconnectedCallback() {
        this.themeObserver?.disconnect();
        this.themeObserver = undefined;
        super.disconnectedCallback();
    }

    private syncDarkMode() {
        this.darkMode =
            document.documentElement.classList.contains("sl-theme-dark");
    }

    updated() {
        if (this.flatMode) {
            this.renderRoot
                .querySelector(`[data-index="${this.selection.itemIndex}"]`)
                ?.scrollIntoView({ block: "nearest" });
            return;
        }
        if (this.items.length === 1) {
            this.openSubmenu();
        }
    }

    private get itemsPerGroup() {
        return Object.groupBy(sortItems(this.items), (item) =>
            item.group === "base" ? "" : item.group
        );
    }

    private get groupKeys() {
        return Object.keys(this.itemsPerGroup);
    }

    private get currentGroupKey() {
        return this.groupKeys.at(this.selection.groupIndex);
    }

    private get currentGroup() {
        return this.currentGroupKey !== undefined
            ? this.itemsPerGroup[this.currentGroupKey]
            : undefined;
    }

    private get currentItem() {
        return this.currentGroup?.[this.selection.itemIndex];
    }

    private get currentSelector() {
        return (
            `[data-group="${this.currentGroupKey}"]` +
            `[data-index="${this.selection.itemIndex}"]`
        );
    }

    private get currentDom() {
        return this.renderRoot.querySelector(this.currentSelector);
    }

    private get subMenuSlPopup() {
        return this.currentDom?.shadowRoot?.querySelector<SlPopup>("sl-popup");
    }

    private get subInsertSubmenu() {
        const submenu = this.currentDom?.querySelector<SubMenu>(
            "c-node-insert-submenu"
        );
        return submenu instanceof SubMenu ? submenu : null;
    }

    down() {
        if (this.flatMode) {
            const list = this.filteredFlatItems;
            if (!list?.length) {
                return;
            }
            this.selection = {
                groupIndex: 0,
                itemIndex: (this.selection.itemIndex + 1) % list.length,
            };
            return;
        }

        if (this.subMenuSlPopup?.active) {
            this.subInsertSubmenu.down();
            return;
        }

        if (!this.currentGroup) {
            return;
        }
        if (this.selection.itemIndex < this.currentGroup.length - 1) {
            // Still room within current group.
            this.selection.itemIndex++;
        } else {
            if (this.selection.groupIndex < this.groupKeys.length - 1) {
                // Jump to next group.
                this.selection.groupIndex++;
                this.selection.itemIndex = 0;
            } else {
                // Begin from start.
                this.selection = { groupIndex: 0, itemIndex: 0 };
            }
        }
        this.requestUpdate();
        return this.currentItem;
    }

    up() {
        if (this.flatMode) {
            const list = this.filteredFlatItems;
            if (!list?.length) {
                return;
            }
            this.selection = {
                groupIndex: 0,
                itemIndex:
                    (this.selection.itemIndex - 1 + list.length) % list.length,
            };
            return;
        }

        if (this.subMenuSlPopup?.active) {
            this.subInsertSubmenu.up();
            return;
        }

        if (!this.currentGroup) {
            return;
        }
        if (this.selection.itemIndex > 0) {
            // Still room within current group.
            this.selection.itemIndex--;
        } else {
            if (this.selection.groupIndex > 0) {
                // Jump to previous group.
                this.selection.groupIndex--;
                this.selection.itemIndex = (this.currentGroup?.length ?? 1) - 1;
            } else {
                // Wrap around to the end.
                this.selection.groupIndex = this.groupKeys.length - 1;
                this.selection.itemIndex = (this.currentGroup?.length ?? 1) - 1;
            }
        }
        this.requestUpdate();
        return this.currentItem;
    }

    openSubmenu() {
        if (this.flatMode) {
            // Flat filtered items are always leaves.
            return false;
        }
        if (this.subMenuSlPopup?.active) {
            return this.subInsertSubmenu?.openSubmenu() || false;
        }

        if (
            this.currentItem?.type === "dropdown" &&
            this.subMenuSlPopup &&
            !this.subMenuSlPopup.active
        ) {
            this.closeAllSubmenus();
            this.subMenuSlPopup.active = true;
            this.subMenuSlPopup.focus();
            return true;
        }
        return false;
    }

    closeSubmenu() {
        if (this.subMenuSlPopup?.active) {
            if (this.subInsertSubmenu?.closeSubmenu()) {
                return true;
            }
            this.subMenuSlPopup.active = false;
            return true;
        }
        return false;
    }

    closeAllSubmenus() {
        this.renderRoot.querySelectorAll("sl-menu-item").forEach((element) => {
            const popup =
                element.shadowRoot?.querySelector<SlPopup>("sl-popup");
            if (popup) {
                popup.active = false;
            }
        });
    }

    runSelection(event: Event) {
        if (this.flatMode) {
            this.filteredFlatItems?.[this.selection.itemIndex]?.run(
                event,
                this.input
            );
            return;
        }
        if (!this.currentItem) {
            return;
        }
        if (this.currentItem.type === "dropdown") {
            if (this.subMenuSlPopup?.active) {
                this.subInsertSubmenu.runSelection(event);
            } else {
                this.openSubmenu();
            }
        } else {
            this.currentItem.run(event, this.input);
        }
    }

    private renderFlat() {
        const list = this.filteredFlatItems;
        const theme = this.darkMode ? "sl-theme-dark" : "sl-theme-light";
        if (!list) {
            return html`<sl-menu class="${theme} flat-list">
                <div class="flat-empty">Loading…</div>
            </sl-menu>`;
        }
        if (!list.length) {
            return html`<sl-menu class="${theme} flat-list">
                <div class="flat-empty">Nothing found.</div>
            </sl-menu>`;
        }
        return html`
            <sl-menu class="${theme} flat-list">
                ${repeat(
                    list,
                    (item) => item.key,
                    (item, itemIndex) =>
                        html`<sl-menu-item
                            @mouseenter=${() => {
                                this.selection = { groupIndex: 0, itemIndex };
                                this.editorView.focus();
                            }}
                            @click=${(e: MouseEvent) => {
                                item.run(e, this.input);
                            }}
                            class=${classMap({
                                selected:
                                    itemIndex === this.selection.itemIndex,
                            })}
                            title=${item.label}
                            data-index=${itemIndex}
                        >
                            ${item.icon
                                ? iconTemplate({ ...item.icon, slot: "prefix" })
                                : nothing}
                            ${item.label}
                            ${item.group
                                ? html`<span
                                      slot="suffix"
                                      class="flat-item-group"
                                      >${item.group}</span
                                  >`
                                : nothing}
                        </sl-menu-item>`
                )}
            </sl-menu>
        `;
    }

    render() {
        if (this.flatMode) {
            return this.renderFlat();
        }
        return html`
            <sl-menu
                class=${this.darkMode ? "sl-theme-dark" : "sl-theme-light"}
            >
                ${join(
                    map(
                        Object.entries(this.itemsPerGroup),
                        ([group, items], groupIndex) => {
                            return html`${group
                                ? html`<sl-menu-label>${group}</sl-menu-label>`
                                : nothing}
                            ${repeat(
                                items,
                                (item) => item.name,
                                (item, itemIndex) =>
                                    html`<sl-menu-item
                                        ?disabled=${item.disabled}
                                        @mouseenter=${() => {
                                            this.selection = {
                                                groupIndex,
                                                itemIndex,
                                            };
                                            this.openSubmenu();
                                            this.editorView.focus();
                                        }}
                                        @focus=${() => {
                                            this.selection = {
                                                groupIndex,
                                                itemIndex,
                                            };
                                            this.editorView.focus();
                                        }}
                                        @click=${(e: MouseEvent) => {
                                            if (item.run) {
                                                item.run(e, this.input);
                                            }
                                        }}
                                        class=${classMap({
                                            selected:
                                                groupIndex ===
                                                    this.selection.groupIndex &&
                                                itemIndex ===
                                                    this.selection.itemIndex,
                                        })}
                                        title=${item.label}
                                        data-group=${group}
                                        data-index=${itemIndex}
                                    >
                                        ${item.icon
                                            ? iconTemplate({
                                                  ...item.icon,
                                                  slot: "prefix",
                                              })
                                            : nothing}
                                        ${item.label || ""}
                                        ${item.type === "dropdown"
                                            ? html`
                                                  <c-node-insert-submenu
                                                      slot="submenu"
                                                      .input=${this.input}
                                                      .item=${item}
                                                  ></c-node-insert-submenu>
                                              `
                                            : nothing}
                                    </sl-menu-item>`
                            )}`;
                        }
                    ),
                    html`<sl-divider></sl-divider>`
                )}
            </sl-menu>
        `;
    }

    static styles = [
        unsafeCSS(shoelaceLight),
        unsafeCSS(shoelaceDark),
        css`
            sl-menu-item:focus-visible::part(base),
            sl-menu-item.selected::part(base) {
                background: var(--stekz-secondary-background);
                color: var(--text-color);
            }

            sl-menu-item.selected[disabled]::part(base) {
                background-color: var(--sl-color-neutral-100);
                color: var(--text-color);
            }

            sl-menu.flat-list {
                max-height: 40vh;
                overflow-y: auto;
            }

            .flat-empty {
                padding: 0.4rem 1rem;
                color: var(--sl-color-neutral-500);
                font-size: var(--sl-font-size-small);
            }

            .flat-item-group {
                margin-left: 1rem;
                color: var(--sl-color-neutral-500);
                font-size: var(--sl-font-size-x-small);
            }
        `,
    ];
}

export { NodeInsertMenu };
